import { useAppSelector } from "@/shared/lib/redux"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Table } from "@/shared/ui/table"
import { ColumnDef } from "@tanstack/react-table"
import { ITradingTransaction } from "@/shared/api/trading"
import { fromNanoToUnit } from "@/entities/bonus"
import { toLocaleString } from "@/shared/lib/to-locale-string"
import { selectSocialMarketPartners, selectTransactions, selectTransactionsDeals, selectTransactionsLoading } from "../../model"
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"

const transactionTypeNames = {
    "client": "Перевод",
    "deal": "Сделка",
    "fee": "Комиссия"
}

export const TransactionsTable = () => {
    const isLoading = useAppSelector(selectTransactionsLoading)
    const transactions = useAppSelector(selectTransactions)
    const deals = useAppSelector(selectTransactionsDeals)
    const partners = useAppSelector(selectSocialMarketPartners)

    const columns: ColumnDef<ITradingTransaction, any>[] = [
        {
            accessorFn: (transaction) => transaction,
            id: 'inn',
            cell: (info) => {
                const transaction: ITradingTransaction = info.getValue()
                return (
                    <div className="font-normal">
                        {partners.find(partner => partner.wallet_id === transaction.counterparty_id)?.inn ?? "—"}
                    </div>
                )
            },
            enableSorting: false,
            header: () => <div className="!text-start">ИНН Партнера</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            id: 'community',
            cell: (info) => {
                const transaction: ITradingTransaction = info.getValue()
                return (
                    <div className="font-normal">
                        {partners.find(partner => partner.wallet_id === transaction.counterparty_id)?.name ?? "—"}
                    </div>
                )
            },
            enableSorting: false,
            header: () => <div className="!text-start">Партнер</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            id: 'type',
            cell: info => {
                const transaction: ITradingTransaction = info.getValue()
                return (
                    <div className="text-color-primary uppercase">
                        {transactionTypeNames[transaction.dest_type_str]}
                    </div>
                )
            },
            enableSorting: false,
            header: () => <div className="!text-start">Тип операции</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            id: 'amount',
            cell: info => {
                const transaction: ITradingTransaction = info.getValue()
                return (
                    <div className="!w-fit ml-auto">
                        {toLocaleString(Math.abs(transaction.amount))}
                    </div>
                )
            },
            meta: {
                cellProps: {
                    className: 'w-full [&_div]:!justify-end'
                }
            },
            sortingFn: (a, b) => {
                return a.original.amount_nano > b.original.amount_nano ? 1 : -1
            },
            header: () => <div>Сумма транзакции, руб</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            id: 'discount',
            cell: info => {
                const transaction: ITradingTransaction = info.getValue()
                const deal = deals[transaction.reason_id]
                return (
                    <div className="!w-fit ml-auto">
                        {transaction.dest_type_str === "deal"
                            ? deal.price ?? 0
                            : "—"
                        }
                    </div>
                )
            },
            meta: {
                cellProps: {
                    className: 'w-full [&_div]:!justify-end'
                }
            },
            sortingFn: (a, b) => {
                return a.original.amount > b.original.amount ? 1 : -1
            },
            header: () => <div>КУРС</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            id: 'amount_nano',
            cell: info => {
                const transaction: ITradingTransaction = info.getValue()
                const deal = deals[transaction.reason_id]
                return (
                    <div className="!w-fit ml-auto">
                        {transaction.dest_type_str === "deal"
                            ? toLocaleString(parseFloat((Math.abs(transaction.amount) * (1 - (deal.price ?? 0))).toFixed(2)))
                            : "—"
                        }
                    </div>
                )
            },
            meta: {
                cellProps: {
                    className: 'w-full [&_div]:!justify-end'
                }
            },
            sortingFn: (a, b) => {
                const dealA = deals[a.original.reason_id]
                const dealB = deals[b.original.reason_id]
                return parseFloat((a.original.amount * (1 - (dealA.price ?? 0))).toFixed(2)) > parseFloat((b.original.amount * (1 - (dealB.price ?? 0))).toFixed(2)) ? 1 : -1
            },
            header: () => <div>Бонусная скидка</div>,
        },
        {
            accessorFn: (transaction) => transaction,
            id: 'tax',
            cell: info => {
                const transaction: ITradingTransaction = info.getValue()
                return (
                    <div className="!w-fit ml-auto">
                        {transaction.dest_type_str === "deal"
                            ? toLocaleString(fromNanoToUnit(Math.abs(transaction.amount_nano * 0.2)))
                            : "—"
                        }
                    </div>
                )
            },
            meta: {
                cellProps: {
                    className: 'w-full [&_div]:!justify-end'
                }
            },
            sortingFn: (a, b) => {
                return Math.abs(a.original.amount_nano * 0.2) > Math.abs(b.original.amount_nano * 0.2) ? 1 : -1
            },
            header: () => <div>НДС, руб</div>,
        },
        {
            accessorFn: (transaction) => (
                <div className="text-nowrap !text-end">
                    {toLocaleDateTimeString(transaction.created_date)}
                </div>
            ),
            id: 'date',
            cell: info => info.getValue(),
            sortingFn: (a, b) => {
                return new Date(a.original.created_date).getTime() > new Date(b.original.created_date).getTime() ? 1 : -1
            },
            meta: {
                cellProps: {
                    className: 'w-full [&_div]:!justify-end'
                }
            },
            header: () => <div className="text-nowrap">Дата операции</div>,
        },
    ]

    return (
        <>
            {Boolean(transactions.length) &&
                <div className="table_box">
                    <Table
                        columns={columns}
                        data={transactions}
                        classNames={{
                            tbody: '[&>*:nth-child(even)]:bg-color-body'
                        }}
                    />
                </div>
            }
            {(!Boolean(transactions.length) && isLoading) &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
            {(!Boolean(transactions.length) && !isLoading) &&
                <NoTableData label="Нет данных о транзакциях" />
            }
        </>
    )
}